import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "@/context/UserContext";
import { useState } from "react";
import { Menu, X, LogOut, User, Map, Bell, Home } from "lucide-react";
import PushNotificationBanner from "@/components/PushNotificationBanner";
import ProfileCompletionGate from "@/components/ProfileCompletionGate";

export function IFLogo({ size = "md" }) {
  const sizes = {
    sm: "w-8 h-8 text-sm",
    md: "w-10 h-10 text-base",
    lg: "w-[126px] h-[126px] text-5xl",
    "lg-text": "w-[72px] h-[72px] text-4xl",  // matched to header text-4xl height
    xl: "w-20 h-20 text-3xl",   // doppelt so groß wie md — für Landing-Hero
  };
  return (
    <div className={`${sizes[size]} rounded-full bg-[#14B8A6] flex items-center justify-center font-bold text-gray-900 tracking-tighter flex-shrink-0`}>
      IF
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isDeaf = user?.user_type === "client";
  const isInterpreter = user?.user_type === "interpreter";

  const DEAF_LINKS = [
    { to: "/kunde/suche", label: "Karte & Suche", icon: Map },
    { to: "/kunde/anfragen", label: "Meine Anfragen", icon: Bell },
    { to: "/kunde/profil", label: "Profil", icon: User },
  ];
  const INTERPRETER_LINKS = [
    { to: "/dolmetscher/dashboard", label: "Dashboard", icon: Home },
    { to: "/dolmetscher/profil", label: "Mein Profil", icon: User },
  ];

  let navLinks = [];
  if (isDeaf) navLinks = DEAF_LINKS;
  else if (isInterpreter) navLinks = INTERPRETER_LINKS;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[74px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <IFLogo size="md" />
              <div>
                <p className="font-bold text-gray-900 leading-none text-base">Interpreter Finder</p>
                <p className="text-sm text-gray-500 leading-none mt-1">Gebärdensprache</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    location.pathname === to
                      ? "bg-[#14B8A6] text-gray-900"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={16} />
                  {label}
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {user.name}
                    <span className="ml-2 text-xs bg-[#FEF3CE] text-[#CA8A04] px-2 py-0.5 rounded-full font-medium">
                      {user.user_type === "client" ? "Kund:in" : "Dolmetscher:in"}
                    </span>
                  </span>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition-colors px-3 py-2 rounded-xl hover:bg-red-50"
                  >
                    <LogOut size={16} />
                    Abmelden
                  </button>
                </div>
              )}
              {!user && (
                <div className="hidden md:flex gap-2">
                  <Link to="/login" className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors">
                    Anmelden
                  </Link>
                  <Link to="/register" className="px-4 py-2 text-sm font-medium bg-[#14B8A6] text-gray-900 rounded-xl hover:bg-[#0D9488] transition-colors">
                    Registrieren
                  </Link>
                </div>
              )}
              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-xl hover:bg-gray-100"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  location.pathname === to ? "bg-[#14B8A6] text-gray-900" : "text-gray-600"
                }`}
              >
                <Icon size={16} />
                {label}
              </Link>
            ))}
            {user && (
              <button
                onClick={() => { setMobileOpen(false); handleLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-red-500"
              >
                <LogOut size={16} />
                Abmelden
              </button>
            )}
            {!user && (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm rounded-xl text-gray-700">Anmelden</Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm rounded-xl bg-[#14B8A6] text-gray-900 text-center font-medium">Registrieren</Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Push notification banner */}
      {user && <PushNotificationBanner />}

      {/* Force-complete profile for clients without a name (so IO never sees "Unknown customer") */}
      {user && <ProfileCompletionGate />}

      {/* Main content */}
      <main>{children}</main>
    </div>
  );
}
